import { useRef, useState } from "react";
import { Form } from "react-bootstrap";
import Select from "components/Ui/SelectField/SelectField";
import imageCompression from "browser-image-compression";
import { useGetVendorsDropdown } from "hooks/useVendors";

const MAX_IMAGES = 20;

// Dense multi-column menus lose readability when crushed. Be generous: only
// downscale if the file is bigger than the API's 20 MB per-image limit, and
// keep enough pixels for small Arabic glyphs to remain crisp.
const API_MAX_IMAGE_BYTES = 20 * 1024 * 1024;
const compressImage = async (file) => {
    if (file.size <= API_MAX_IMAGE_BYTES) return file;
    try {
        const compressed = await imageCompression(file, {
            maxWidthOrHeight: 5000,
            maxSizeMB: 18,
            initialQuality: 0.92,
            useWebWorker: true,
        });
        return new File([compressed], file.name, { type: file.type });
    } catch (err) {
        console.warn("image compression failed, using original", err);
        return file;
    }
};

const UploadView = ({
    vendorId,
    setVendorId,
    images,
    setImages,
    imageThumbs,
    onExtract,
    submitting,
}) => {
    const { vendors } = useGetVendorsDropdown();
    const [dragging, setDragging] = useState(false);
    const fileInputRef = useRef(null);

    const vendorOptions = (vendors ?? []).map((v) => ({ value: v.id, label: v.name }));
    const selectedVendor = vendorOptions.find((o) => o.value === vendorId) ?? null;

    const addFiles = async (incoming) => {
        const accepted = Array.from(incoming).filter((f) => f.type?.startsWith("image/"));
        const remaining = MAX_IMAGES - images.length;
        const trimmed = accepted.slice(0, Math.max(0, remaining));
        const compressed = await Promise.all(trimmed.map(compressImage));
        setImages((prev) => [...prev, ...compressed]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        if (e.dataTransfer?.files?.length) {
            addFiles(e.dataTransfer.files);
        }
    };

    const handleFileInput = (e) => {
        if (e.target.files?.length) {
            addFiles(e.target.files);
            // Reset so selecting the same file again still fires onChange.
            e.target.value = "";
        }
    };

    const handleRemove = (idx) => {
        setImages((prev) => prev.filter((_, i) => i !== idx));
    };

    const canExtract = vendorId && images.length > 0 && !submitting;

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <Form.Group className="mb-3" controlId="vendor">
                        <Form.Label>
                            المطعم<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <Select
                            options={vendorOptions}
                            value={selectedVendor}
                            placeholder="اختر المطعم"
                            onChange={(s) => setVendorId(s?.value ?? null)}
                            isSearchable
                            isDisabled={submitting}
                        />
                    </Form.Group>
                </div>
            </div>

            <Form.Group className="mb-3">
                <Form.Label>
                    صور القائمة<span style={{ color: "red" }}>*</span>{" "}
                    <small style={{ color: "#888" }}>
                        ({images.length}/{MAX_IMAGES})
                    </small>
                </Form.Label>

                <div
                    className={`dropzone ${dragging ? "active" : ""}`}
                    onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragging(true);
                    }}
                    onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragging(false);
                    }}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                >
                    <p>اسحب صور القائمة هنا أو اضغط للاختيار</p>
                    <div className="hint">
                        JPEG، PNG، WebP — حتى {MAX_IMAGES} صورة، 10 ميجا للصورة
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleFileInput}
                    />
                </div>

                {imageThumbs.length > 0 ? (
                    <div className="selected-images">
                        {imageThumbs.map((thumb, idx) => (
                            <div key={`${thumb.file.name}-${idx}`} className="selected-image-thumb">
                                <img src={thumb.url} alt={thumb.file.name} />
                                <button
                                    type="button"
                                    className="remove"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRemove(idx);
                                    }}
                                    title="إزالة"
                                    disabled={submitting}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                ) : null}
            </Form.Group>

            <div className="actions">
                <button
                    type="button"
                    className="button"
                    onClick={onExtract}
                    disabled={!canExtract}
                    style={{
                        minWidth: 220,
                        opacity: canExtract ? 1 : 0.6,
                        cursor: canExtract ? "pointer" : "not-allowed",
                    }}
                >
                    {submitting ? "جارٍ الاستخراج..." : "ابدأ الاستخراج"}
                </button>
            </div>
        </>
    );
};

export default UploadView;
